/**
 * StoreKit 2 / Google Play Billing through `@capgo/native-purchases` (v8), loaded from
 * the native entry chunk (NativeLoaders.store). The platform sheet is the payment
 * confirmation; the API grants (Flow H):
 *
 * 1. purchaseProduct with the player id as appAccountToken (StoreKit, a UUID) /
 *    obfuscatedAccountId (Play) and auto-finish off, so nothing is finished yet;
 * 2. the transaction goes into a KV journal first, then to POST /v1/purchases/verify
 *    `{ provider, productId, receipt, idemKey }`: receipt = the StoreKit 2 JWS (Apple)
 *    or the purchase token (Google), idemKey derived from the transaction, so every
 *    retry grants once;
 * 3. only `granted` finishes it: StoreKit finish, Play consume (one-time products: the
 *    server owns entitlements) or Play acknowledge (subscriptions). `pending`, `blocked`
 *    and errors keep it in the journal for resume(); `failed` leaves it unfinished
 *    (Play refunds what is not acknowledged within 3 days).
 *
 * Ask to Buy and Play "pending" payments resolve `pending` (reason `deferred`) and
 * complete later: iOS through the plugin's transactionUpdated event (listened to from
 * the first call), Android through getPurchases() in resume(). The plugin finishes
 * StoreKit transactions that arrive through Transaction.updates (Ask to Buy approvals,
 * unfinished ones at launch) before it reports them, and drops the event when nobody
 * listens yet; so the journal keeps each signed transaction until the API has
 * answered, and resume() also verifies StoreKit transactions the API never answered
 * for (a finished consumable is only listed with SKIncludeConsumableInAppPurchaseHistory).
 */
import { ROUTES, type ApiClient } from '../api.js';
import { b64url, utf8 } from '../auth/b64.js';
import type { Loader } from '../lazy.js';
import type { PurchaseResponse, PurchaseVerify, StoreResponse } from '../schemas.js';
import { readJson, writeJson, type KV } from '../storage.js';
import { systemClock, type Clock } from '../time.js';
import { confirmFlag, fromResponse, withUsdPrice, type IapProduct, type IapProvider, type PurchaseOptions, type PurchaseResult } from './types.js';

interface Handle {
  remove(): Promise<void>;
}
/** PURCHASE_TYPE values */
export type NativeProductType = 'inapp' | 'subs';

/** The fields of the plugin's Transaction used here. */
export interface NativeTransaction {
  /** StoreKit transaction id; on Android the purchase token */
  readonly transactionId: string;
  readonly productIdentifier: string;
  /** StoreKit 2 signed transaction (iOS) */
  readonly jwsRepresentation?: string;
  /** Play purchase token (Android) */
  readonly purchaseToken?: string;
  /** Play Purchase.PurchaseState: "1" purchased, anything else not paid yet */
  readonly purchaseState?: string;
  readonly isAcknowledged?: boolean;
  readonly appAccountToken?: string | null;
}
export interface NativeProduct {
  readonly identifier: string;
  readonly priceString: string;
}

/** The part of NativePurchasesPlugin used here, as in its definitions.d.ts. */
export interface NativeStorePlugin {
  getProducts(o: { productIdentifiers: string[]; productType?: NativeProductType }): Promise<{ products: NativeProduct[] }>;
  purchaseProduct(o: {
    productIdentifier: string;
    planIdentifier?: string;
    productType?: NativeProductType;
    quantity?: number;
    appAccountToken?: string;
    isConsumable?: boolean;
    autoAcknowledgePurchases?: boolean;
  }): Promise<NativeTransaction>;
  getPurchases(o?: { productType?: NativeProductType; appAccountToken?: string; onlyCurrentEntitlements?: boolean }): Promise<{ purchases: NativeTransaction[] }>;
  restorePurchases(): Promise<void>;
  /** Play acknowledge; StoreKit finish (purchaseToken = transaction id) */
  acknowledgePurchase(o: { purchaseToken: string }): Promise<void>;
  /** Play only */
  consumePurchase(o: { purchaseToken: string }): Promise<void>;
  addListener(event: 'transactionUpdated', fn: (t: NativeTransaction) => void): Promise<Handle>;
}
export interface NativeStoreModule {
  NativePurchases: NativeStorePlugin;
}

export interface NativeIapOptions {
  api: ApiClient;
  /** the journal of transactions the API has not settled yet */
  kv: KV;
  store: 'apple' | 'google';
  load: Loader<NativeStoreModule>;
  /** the player's UUID: StoreKit appAccountToken / Play obfuscatedAccountId, checked by the API against the caller */
  playerId?: () => string | null;
  /** Play subscriptions: product id -> base plan id (Market Plus) */
  subscriptionPlans?: Record<string, string>;
  now?: Clock;
}

/** A transaction the API has not settled yet. */
interface OpenTx {
  productId: string;
  transactionId: string;
  receipt: string;
  idemKey: string;
  confirm: boolean;
  /** what finishes it: the StoreKit transaction id or the Play purchase token */
  finishToken: string;
  /** Play: consume (one-time products) instead of acknowledge; false once Play reports it acknowledged */
  consume: boolean;
  needsFinish: boolean;
  status: 'unverified' | 'pending' | 'blocked';
  at: number;
}

const JOURNAL_KEY = 'iap.native.open';
/** StoreKit transaction ids the API has answered for (iOS), newest last */
const SETTLED_KEY = 'iap.native.settled';
const SETTLED_MAX = 500;
const PURCHASED = '1';

function errorParts(e: unknown): { code: string; message: string } {
  const { code, message } = (e ?? {}) as { code?: unknown; message?: unknown };
  return { code: String(code ?? ''), message: String(message ?? e) };
}

async function sha256(s: string): Promise<string> {
  return b64url(new Uint8Array(await crypto.subtle.digest('SHA-256', utf8(s))));
}

export class NativeIapProvider implements IapProvider {
  private ready: Promise<{ NativePurchases: NativeStorePlugin }> | null = null;
  private updates: Handle | null = null;
  private readonly inflight = new Map<string, Promise<PurchaseResult>>();
  private readonly listeners = new Set<(r: PurchaseResult) => void>();

  constructor(private readonly o: NativeIapOptions) {}

  get provider(): 'apple' | 'google' {
    return this.o.store;
  }

  /** Server catalog, priced in local currency by the platform's price tiers (MO-03.1). */
  async products(): Promise<IapProduct[]> {
    const [catalog, { NativePurchases: store }] = await Promise.all([this.o.api.call<StoreResponse>('GET', ROUTES.store), this.plugin()]);
    const priced = catalog.products.filter((p) => p.priceUsdCents !== null).map((p) => p.id);
    const subs = priced.filter((id) => this.planOf(id) !== undefined);
    const inapp = priced.filter((id) => this.planOf(id) === undefined);
    // one query at a time: the Play billing client does not support concurrent queries
    const local = new Map<string, string>();
    for (const [ids, productType] of [[inapp, 'inapp'], [subs, 'subs']] as const) {
      if (!ids.length) continue;
      const { products } = await store.getProducts({ productIdentifiers: ids, ...(productType === 'subs' && this.o.store === 'google' ? { productType } : {}) });
      for (const p of products) local.set(p.identifier, p.priceString);
    }
    return catalog.products.map((p) => {
      const usd = withUsdPrice(p);
      return { ...usd, displayPrice: local.get(p.id) ?? usd.displayPrice };
    });
  }

  async purchase(productId: string, opts: PurchaseOptions = {}): Promise<PurchaseResult> {
    const { NativePurchases: store } = await this.plugin();
    const player = this.o.playerId?.();
    const plan = this.planOf(productId);
    const bought = await store
      .purchaseProduct({
        productIdentifier: productId,
        quantity: 1,
        ...(plan !== undefined && this.o.store === 'google' ? { productType: 'subs' as const, planIdentifier: plan } : {}),
        ...(player ? { appAccountToken: player } : {}),
        // nothing is consumed, acknowledged or finished before the API grants
        isConsumable: false,
        autoAcknowledgePurchases: false,
      })
      .then(
        (tx) => ({ tx }),
        (error: unknown) => ({ error }),
      );
    if ('error' in bought) return this.storeError(productId, bought.error);
    return this.settle(await this.open(bought.tx, confirmFlag(opts).confirm === true));
  }

  /**
   * "Restore purchases": StoreKit sync + current entitlements (iOS) or the Play purchase
   * list, each verified again (the API answers `granted` once more for a receipt it
   * already granted to this player), plus everything resume() retries.
   */
  async restore(): Promise<PurchaseResult[]> {
    const { NativePurchases: store } = await this.plugin();
    if (this.o.store === 'apple') await store.restorePurchases();
    const { purchases } = await store.getPurchases(this.o.store === 'apple' ? { onlyCurrentEntitlements: true } : undefined);
    const results: PurchaseResult[] = [];
    for (const p of purchases) if (p.purchaseState === undefined || p.purchaseState === PURCHASED) results.push(await this.settle(await this.open(p, true)));
    return [...results, ...(await this.resumeAll(new Set(purchases.map((p) => p.transactionId))))];
  }

  /** Startup/foreground: settle what is still open. The last grant carries the freshest snapshot. */
  async resume(): Promise<PurchaseResult | null> {
    const all = await this.resumeAll();
    return all.filter((r) => r.status === 'granted').at(-1) ?? all[0] ?? null;
  }

  /**
   * Settle everything still open. The player has paid for all of it, so the MO-06
   * confirmation travels as given.
   * - Android: Play's list of unacknowledged purchases is the truth (pending payments
   *   are reported, not verified); journal entries Play no longer lists were consumed
   *   or refunded, and are dropped.
   * - iOS: the journal, plus StoreKit transactions the API has not answered for yet
   *   (an Ask to Buy approval that arrived while the game was closed).
   */
  async resumeAll(skip: ReadonlySet<string> = new Set()): Promise<PurchaseResult[]> {
    const { NativePurchases: store } = await this.plugin();
    const google = this.o.store === 'google';
    const { purchases } = await store.getPurchases();
    const settled = new Set(this.settledIds());
    const done = new Set(skip);
    const results: PurchaseResult[] = [];
    for (const p of purchases) {
      if (done.has(p.transactionId) || (google ? p.isAcknowledged === true : settled.has(p.transactionId))) continue;
      done.add(p.transactionId);
      results.push(
        p.purchaseState === undefined || p.purchaseState === PURCHASED
          ? await this.settle(await this.open(p, true))
          : { status: 'pending', provider: this.provider, productId: p.productIdentifier, receipt: p.transactionId, reason: 'deferred' },
      );
    }
    for (const t of Object.values(this.journal())) {
      if (done.has(t.transactionId)) continue;
      if (google) this.forget(t);
      else results.push(await this.settle({ ...t, confirm: true }));
    }
    return results;
  }

  /** Purchases settled outside purchase(): Ask to Buy approvals, renewals, transactions left over at launch (iOS). */
  onTransaction(fn: (r: PurchaseResult) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Stop listening for StoreKit updates. */
  async dispose(): Promise<void> {
    this.listeners.clear();
    await this.updates?.remove();
    this.updates = null;
    this.ready = null;
  }

  /**
   * The plugin inside an object, never on its own: resolving a promise with Capacitor's plugin
   * proxy looks up its `then`, the proxy answers every name with a native call that iOS and
   * Android reject as UNIMPLEMENTED, and the promise never settles (every store call hung).
   */
  private plugin(): Promise<{ NativePurchases: NativeStorePlugin }> {
    this.ready ??= this.o
      .load()
      .then(async ({ NativePurchases }) => {
        if (this.o.store === 'apple') this.updates = await NativePurchases.addListener('transactionUpdated', (t) => void this.onUpdate(t));
        return { NativePurchases };
      })
      .catch((e: unknown) => {
        this.ready = null;
        throw e;
      });
    return this.ready;
  }

  private async onUpdate(tx: NativeTransaction): Promise<void> {
    const r = await this.settle(await this.open(tx, true));
    for (const fn of this.listeners) fn(r);
  }

  private planOf(productId: string): string | undefined {
    return this.o.subscriptionPlans?.[productId];
  }

  /**
   * iOS rejects with "User cancelled" / "Transaction pending"; Android with code
   * USER_CANCELED, "Purchase is pending", or ITEM_ALREADY_OWNED when an earlier purchase
   * of the product was never consumed (then that one is settled now).
   */
  private async storeError(productId: string, e: unknown): Promise<PurchaseResult> {
    const { code, message } = errorParts(e);
    if (/cancel/i.test(`${code} ${message}`)) return { status: 'cancelled', provider: this.provider, productId };
    if (/pending/i.test(message)) return { status: 'pending', provider: this.provider, productId, reason: 'deferred' };
    if (code === 'ITEM_ALREADY_OWNED') {
      const earlier = (await this.resumeAll()).find((r) => r.productId === productId);
      if (earlier) return earlier;
    }
    return { status: 'failed', provider: this.provider, productId, reason: code || message };
  }

  private async open(tx: NativeTransaction, confirm: boolean): Promise<OpenTx> {
    const apple = this.o.store === 'apple';
    const token = tx.purchaseToken ?? tx.transactionId;
    return {
      productId: tx.productIdentifier,
      transactionId: tx.transactionId,
      receipt: apple ? (tx.jwsRepresentation ?? '') : token,
      idemKey: apple ? `apple:${tx.transactionId}` : `google:${await sha256(token)}`,
      confirm,
      finishToken: apple ? tx.transactionId : token,
      consume: !apple && this.planOf(tx.productIdentifier) === undefined,
      needsFinish: apple || tx.isAcknowledged !== true,
      status: 'unverified',
      at: (this.o.now ?? systemClock)(),
    };
  }

  /** One verification per transaction at a time (a purchase and a StoreKit update may race). */
  private settle(t: OpenTx): Promise<PurchaseResult> {
    let p = this.inflight.get(t.transactionId);
    if (!p) {
      p = this.verify(t).finally(() => this.inflight.delete(t.transactionId));
      this.inflight.set(t.transactionId, p);
    }
    return p;
  }

  private async verify(t: OpenTx): Promise<PurchaseResult> {
    const base = { provider: this.provider, productId: t.productId, receipt: t.transactionId };
    if (!t.receipt) return { ...base, status: 'failed', reason: 'no_signed_transaction' };
    this.record(t);
    const body: PurchaseVerify = { provider: this.o.store, productId: t.productId, receipt: t.receipt, idemKey: t.idemKey, ...(t.confirm ? { confirm: true } : {}) };
    const res = await this.o.api.call<PurchaseResponse>('POST', ROUTES.purchaseVerify, body).catch(() => null);
    if (!res) return { ...base, status: 'pending', reason: 'unverified' }; // offline or API error: resume() retries
    if (res.status === 'granted') {
      // a finish that fails stays in the journal: the next verify answers granted again, then finishes
      if (!t.needsFinish || (await this.finish(t).then(() => true, () => false))) this.forget(t, true);
    } else if (res.status === 'failed') {
      this.forget(t, true);
    } else {
      this.record({ ...t, status: res.status });
    }
    return fromResponse(this.o.store, t.productId, res, t.transactionId);
  }

  private async finish(t: OpenTx): Promise<void> {
    const { NativePurchases: store } = await this.plugin();
    if (this.o.store === 'google') {
      await (t.consume ? store.consumePurchase({ purchaseToken: t.finishToken }) : store.acknowledgePurchase({ purchaseToken: t.finishToken }));
      return;
    }
    // StoreKit rejects a transaction the plugin already finished (Transaction.updates); that is the goal anyway
    await store.acknowledgePurchase({ purchaseToken: t.finishToken }).catch(() => undefined);
  }

  private journal(): Record<string, OpenTx> {
    return readJson<Record<string, OpenTx>>(this.o.kv, JOURNAL_KEY) ?? {};
  }

  private record(t: OpenTx): void {
    writeJson(this.o.kv, JOURNAL_KEY, { ...this.journal(), [t.transactionId]: t });
  }

  /** Drop from the journal; `answered`: the API granted or refused it, so resume() leaves the StoreKit transaction alone. */
  private forget(t: OpenTx, answered = false): void {
    const { [t.transactionId]: _gone, ...rest } = this.journal();
    writeJson(this.o.kv, JOURNAL_KEY, rest);
    if (answered && this.o.store === 'apple') writeJson(this.o.kv, SETTLED_KEY, [...this.settledIds().filter((id) => id !== t.transactionId), t.transactionId].slice(-SETTLED_MAX));
  }

  private settledIds(): string[] {
    return readJson<string[]>(this.o.kv, SETTLED_KEY) ?? [];
  }
}
